import React, { useState, useEffect, useContext } from 'react';
import { CartContext } from '../context/CartContext';

const Cart = ({ 
    isOpen, 
    onClose, 
    onCheckout,
    onShowDialog,
    homeDeliveryEnabled,
    dineInEnabled,
    takeawayEnabled = true,
    whatsappEnabled = false,
    couponsEnabled,
    coupons = [],
    restaurantInfo,
    isRestaurantOpen,
    deliveryCharge = 50.00,
    freeDeliveryOver = 500.00,
    menuData = [],
    onAddToCart,
    showOnlyVeg,
    isWaiterMode,
    initialTableNumber
}) => {
    const { cart, dispatch, ActionTypes } = useContext(CartContext);

    // Dynamic auto-suggestions recommendation engine (advanced)
    const getSuggestions = () => {
        if (!menuData || !Array.isArray(menuData) || cart.length === 0) return [];

        const cartItemIds = new Set(cart.map(item => String(item.item_id)));
        
        // Detect current categories in cart to prevent redundant category suggestions
        const hasBeverageInCart = cart.some(item => {
            const itemCat = (item.category || '').toLowerCase();
            return ['beverage', 'drink', 'shake', 'mocktail', 'cold drink'].some(kw => itemCat.includes(kw));
        });
        const hasDessertInCart = cart.some(item => {
            const itemCat = (item.category || '').toLowerCase();
            return ['dessert', 'sweet', 'ice cream'].some(kw => itemCat.includes(kw));
        });

        const availableItems = [];

        menuData.forEach(category => {
            if (category.items && Array.isArray(category.items)) {
                category.items.forEach(item => {
                    const itemIdStr = String(item.item_id || item.id);
                    if (itemIdStr && !cartItemIds.has(itemIdStr)) {
                        // Filter out non-veg items if showOnlyVeg is active
                        if (showOnlyVeg && item.item_type === 'non-veg') {
                            return;
                        }
                        availableItems.push({
                            ...item,
                            categoryName: category.name
                        });
                    }
                });
            }
        });

        if (availableItems.length === 0) return [];

        const upsellCategories = ['beverage', 'drink', 'shake', 'mocktail', 'dessert', 'side', 'extra', 'addon', 'sweet', 'ice cream', 'cold drink', 'soup'];

        // Score items based on multiple metrics (upsell category, popular/recommended flags, and cart context)
        const scoredItems = availableItems.map(item => {
            let score = 0;
            const catLower = (item.categoryName || '').toLowerCase();
            
            // 1. Base score for matching upsell categories (+10)
            const isUpsell = upsellCategories.some(keyword => catLower.includes(keyword));
            if (isUpsell) score += 10;

            // 2. Score boost for recommended or popular items (+5)
            if (item.is_recommended) score += 5;
            if (item.is_popular) score += 5;

            // 3. Deduct points if user already has this item type (promote other cross-sell classes)
            const isBeverage = ['beverage', 'drink', 'shake', 'mocktail', 'cold drink'].some(kw => catLower.includes(kw));
            const isDessert = ['dessert', 'sweet', 'ice cream'].some(kw => catLower.includes(kw));

            if (isBeverage && hasBeverageInCart) score -= 8;
            if (isDessert && hasDessertInCart) score -= 8;

            return {
                item,
                score
            };
        });

        // Sort by score descending
        scoredItems.sort((a, b) => b.score - a.score);

        // Filter for category diversity (limit to 1 item per category)
        const selectedItems = [];
        const chosenCategories = new Set();

        for (const entry of scoredItems) {
            if (selectedItems.length >= 3) break;
            
            const catName = entry.item.categoryName;
            if (!chosenCategories.has(catName)) {
                selectedItems.push(entry.item);
                chosenCategories.add(catName);
            }
        }

        // Fallback: If diversity restriction yields less than 3, fill up remaining slots from top scored items
        if (selectedItems.length < 3) {
            for (const entry of scoredItems) {
                if (selectedItems.length >= 3) break;
                if (!selectedItems.some(i => String(i.item_id || i.id) === String(entry.item.item_id || entry.item.id))) {
                    selectedItems.push(entry.item);
                }
            }
        }

        return selectedItems;
    };

    const suggestions = getSuggestions();

    const initialOrderType = dineInEnabled ? 'dine-in' : (takeawayEnabled ? 'takeaway' : (homeDeliveryEnabled ? 'home-delivery' : ''));
    const [orderType, setOrderType] = useState(initialOrderType);
    
    useEffect(() => {
        setOrderType(dineInEnabled ? 'dine-in' : (takeawayEnabled ? 'takeaway' : (homeDeliveryEnabled ? 'home-delivery' : '')));
    }, [dineInEnabled, takeawayEnabled, homeDeliveryEnabled]);

    const [customerName, setCustomerName] = useState(localStorage.getItem('smart_menu_customer_name') || '');
    const [customerPhone, setCustomerPhone] = useState(localStorage.getItem('smart_menu_customer_phone') || '');
    const [tableNumber, setTableNumber] = useState(initialTableNumber === 'Takeaway' ? '' : (initialTableNumber || '1'));
    const [deliveryAddress, setDeliveryAddress] = useState('');

    useEffect(() => {
        if (initialTableNumber && initialTableNumber !== 'Takeaway') {
            setTableNumber(initialTableNumber);
        }
    }, [initialTableNumber]);
    const [customerMessage, setCustomerMessage] = useState('');
    const [couponCode, setCouponCode] = useState('');
    const [discount, setDiscount] = useState({ type: 'none', value: 0, code: '' });

    // Loyalty Program States
    const [loyaltyData, setLoyaltyData] = useState(null);
    const [redeemLoyalty, setRedeemLoyalty] = useState(false);
    const [loyaltyPin, setLoyaltyPin] = useState('');
    const [isPinVerified, setIsPinVerified] = useState(false);
    const [verifyingPin, setVerifyingPin] = useState(false);
    
    const [showPaymentModeModal, setShowPaymentModeModal] = useState(false);
    const [pendingOrderDetails, setPendingOrderDetails] = useState(null);

    useEffect(() => {
        const checkLoyalty = async () => {
            if (customerPhone && customerPhone.replace(/\D/g, '').length >= 10 && restaurantInfo?.id) {
                try {
                    const res = await fetch('https://menu.searchisend.in/apimenu/check_loyalty.php', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ customer_id: restaurantInfo.id, phone_number: customerPhone })
                    });
                    const data = await res.json();
                    if (data.success && data.loyalty_active && data.member_found) {
                        setLoyaltyData({
                            points: parseFloat(data.points),
                            rupees_per_point: parseFloat(data.config.rupees_per_point),
                            min_redemption_points: parseFloat(data.config.min_redemption_points),
                            name: data.name
                        });
                        setRedeemLoyalty(false);
                        setLoyaltyPin('');
                        setIsPinVerified(false);
                    } else {
                        setLoyaltyData(null);
                        setRedeemLoyalty(false);
                        setLoyaltyPin('');
                        setIsPinVerified(false);
                    }
                } catch (err) {
                    console.error('Loyalty check failed', err);
                    setLoyaltyData(null);
                    setRedeemLoyalty(false);
                    setLoyaltyPin('');
                    setIsPinVerified(false);
                }
            } else {
                setLoyaltyData(null);
                setRedeemLoyalty(false);
                setLoyaltyPin('');
                setIsPinVerified(false);
            }
        };
        
        if (isOpen) {
            const timer = setTimeout(() => { checkLoyalty(); }, 300);
            return () => clearTimeout(timer);
        }
    }, [customerPhone, restaurantInfo, isOpen]);

    const subtotal = cart.reduce((sum, item) => sum + parseFloat(item.price) * item.quantity, 0);
    const gstPercent = restaurantInfo?.billing_config?.gst_percent !== undefined ? parseFloat(restaurantInfo.billing_config.gst_percent) : 5;
    const svcPercent = restaurantInfo?.billing_config?.service_charge !== undefined ? parseFloat(restaurantInfo.billing_config.service_charge) : 0;
    
    const gst = subtotal * (gstPercent / 100);
    const serviceCharge = subtotal * (svcPercent / 100);

    let deliveryFee = 0;
    const freeDeliveryThreshold = parseFloat(restaurantInfo?.free_delivery_over || 0);
    const actualDeliveryCharge = parseFloat(restaurantInfo?.delivery_charge || 0);
    if (homeDeliveryEnabled && orderType === 'home-delivery' && subtotal > 0) {
        if (freeDeliveryThreshold > 0 && subtotal >= freeDeliveryThreshold) {
            deliveryFee = 0;
        } else {
            deliveryFee = actualDeliveryCharge;
        }
    }
    
    let discountAmount = 0;
    if (discount.type === 'percentage') {
        discountAmount = subtotal * (discount.value / 100);
    } else if (discount.type === 'flat') {
        discountAmount = Math.min(discount.value, subtotal);
    }

    let loyaltyDiscount = 0;
    let loyaltyPointsRedeemed = 0;
    if (redeemLoyalty && isPinVerified && loyaltyData && loyaltyData.points >= loyaltyData.min_redemption_points) {
        loyaltyDiscount = loyaltyData.points * loyaltyData.rupees_per_point;
        const maxDiscount = subtotal + gst + deliveryFee - discountAmount;
        if (loyaltyDiscount > maxDiscount) {
            loyaltyDiscount = maxDiscount;
            loyaltyPointsRedeemed = maxDiscount / loyaltyData.rupees_per_point;
        } else {
            loyaltyPointsRedeemed = loyaltyData.points;
        }
    }

    const total = subtotal + gst + serviceCharge + deliveryFee - discountAmount - loyaltyDiscount;

    const handleUpdateQuantity = (itemId, change) => { dispatch({ type: ActionTypes.UPDATE_QUANTITY, payload: { itemId, change } }); };
    const handleRemoveItem = (itemId) => { dispatch({ type: ActionTypes.REMOVE_FROM_CART, payload: { itemId } }); };
    const handleClearCart = () => { dispatch({ type: ActionTypes.CLEAR_CART }); };

    const handleApplyCoupon = () => {
        const codeToApply = couponCode.toUpperCase();
        const validCoupon = coupons.find(c => c.coupon_code.toUpperCase() === codeToApply);

        if (validCoupon) {
            if (subtotal >= validCoupon.min_order_value) {
                setDiscount({
                    type: validCoupon.discount_type,
                    value: parseFloat(validCoupon.discount_value),
                    code: validCoupon.coupon_code
                });
                onShowDialog(`Coupon "${validCoupon.coupon_code}" applied successfully!`, 'success');
            } else {
                setDiscount({ type: 'none', value: 0, code: '' });
                onShowDialog(`This coupon is only valid on orders over ₹${validCoupon.min_order_value}.`, 'error');
            }
        } else {
            setDiscount({ type: 'none', value: 0, code: '' });
            onShowDialog('Invalid coupon code.', 'error');
        }
    };

    const handleVerifyPin = async () => {
        if (!loyaltyPin || loyaltyPin.length < 4) {
            onShowDialog('Please enter a 4-digit PIN.', 'error');
            return;
        }
        setVerifyingPin(true);
        try {
            const response = await fetch('https://menu.searchisend.in/apimenu/verify_loyalty_pin.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    customer_id: restaurantInfo.id,
                    phone_number: customerPhone,
                    pin: loyaltyPin
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setIsPinVerified(true);
                onShowDialog('PIN verified successfully! Points applied.', 'success');
            } else {
                setIsPinVerified(false);
                onShowDialog(data.message || 'Invalid PIN.', 'error');
            }
        } catch (error) {
            console.error('Error verifying PIN:', error);
            setIsPinVerified(false);
            onShowDialog('Error verifying PIN. Please try again.', 'error');
        } finally {
            setVerifyingPin(false);
        }
    };

    const handleCheckoutClick = () => {
        if (redeemLoyalty && !isPinVerified) {
            onShowDialog('Please verify your 4-digit PIN first to redeem points.', 'error');
            return;
        }
        const orderDetails = {
            orderType, customerName, customerPhone, tableNumber, deliveryAddress, customerMessage,
            items: cart,
            subtotal, gst, serviceCharge, deliveryFee, discountAmount, 
            loyaltyDiscount, loyaltyPointsRedeemed, loyaltyPin,
            total,
            appliedCoupon: discount.code
        };

        if (isWaiterMode) {
            handleWaiterCheckout(orderDetails);
            return;
        }

        // Show payment mode modal instead of direct checkout
        setPendingOrderDetails(orderDetails);
        setShowPaymentModeModal(true);
    };

    const confirmPaymentMode = (mode) => {
        if (mode === 'online') {
            setShowPaymentModeModal(false);
            onCheckout(pendingOrderDetails, 'online');
        } else if (mode === 'whatsapp') {
            setShowPaymentModeModal(false);
            onCheckout(pendingOrderDetails, 'whatsapp');
        } else {
            // Cash/Pay Later
            setShowPaymentModeModal(false);
            onCheckout(pendingOrderDetails, 'cash');
        }
    };

    const handleWaiterCheckout = async (orderDetails) => {
        if (!tableNumber || !customerName || !customerPhone) {
            onShowDialog('Table Number, Customer Name, and Phone are mandatory for Waiter Orders.', 'error');
            return;
        }

        const token = localStorage.getItem('staff_token');
        if (!token) {
            onShowDialog('Waiter session expired. Please login again.', 'error');
            return;
        }

        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_waiter_order.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    table_no: orderDetails.tableNumber,
                    customer_name: orderDetails.customerName,
                    customer_phone: orderDetails.customerPhone,
                    items: orderDetails.items,
                    sub_total: orderDetails.subtotal,
                    tax_total: orderDetails.gst,
                    grand_total: orderDetails.total
                })
            });
            const data = await res.json();
            if (data.success) {
                onShowDialog('Order sent to kitchen successfully!', 'success');
                dispatch({ type: ActionTypes.CLEAR_CART });
                onClose();
            } else {
                onShowDialog(data.message || 'Failed to place order', 'error');
            }
        } catch (err) {
            onShowDialog('Network error while placing order', 'error');
        }
    };
    
    const tableCount = restaurantInfo?.table_count || 15;
    const tableOptions = Array.from({ length: tableCount }, (_, i) => ( <option key={i + 1} value={i + 1}>Table {i + 1}</option> ));

    return (
        <aside className={`cart-sidebar ${isOpen ? 'open' : ''}`}>
            <div className="cart-header">
                <h3>Your Order</h3>
                <button className="cart-close-btn" onClick={onClose}>×</button>
            </div>
    
            <div className="cart-scrollable-content">
                {cart.length === 0 ? (
                    <div className="cart-empty-message">
                        <div className="icon"><i className="fas fa-shopping-cart"></i></div>
                        <p>Your cart is empty</p>
                        <p><small>Add items from the menu to start your order</small></p>
                    </div>
                ) : (
                    <>
                        <div className="cart-body">
                            <ul className="cart-items-list">
                                {cart.map(item => (
                                    <li key={item.item_id} className="cart-item">
                                        <img src={item.image_url} alt={item.name} />
                                        <div className="cart-item-details">
                                            <h4>{item.name}</h4>
                                            <div className="quantity-controls">
                                                <button className="quantity-btn decrease" onClick={() => handleUpdateQuantity(item.item_id, -1)}>-</button>
                                                <span>{item.quantity}</span>
                                                <button className="quantity-btn increase" onClick={() => handleUpdateQuantity(item.item_id, 1)}>+</button>
                                            </div>
                                            <span className="cart-item-price">₹{(parseFloat(item.price) * item.quantity).toFixed(2)}</span>
                                        </div>
                                        <button className="cart-item-remove-btn" onClick={() => handleRemoveItem(item.item_id)}><i className="fas fa-trash-alt"></i></button>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {suggestions.length > 0 && (
                            <div className="cart-suggestions-container" style={{ padding: '15px 20px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-color)' }}>
                                <h4 style={{ margin: '0 0 10px 0', fontSize: '0.85rem', color: 'var(--text-color-secondary)', fontWeight: '700', letterSpacing: '-0.01em', textTransform: 'uppercase', opacity: 0.8 }}>Frequently Added Together</h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {suggestions.map(item => (
                                        <div key={item.item_id || item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', background: 'var(--card-bg-color)', padding: '8px 12px', borderRadius: '12px', border: '1px solid var(--border-color)', boxShadow: 'var(--box-shadow-light)' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                {item.image_url && <img src={item.image_url} alt={item.name} style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover' }} />}
                                                <div>
                                                    <h5 style={{ margin: 0, fontSize: '0.82rem', fontWeight: '600', color: 'var(--text-color-headings)' }}>{item.name}</h5>
                                                    <span style={{ fontSize: '0.78rem', color: 'var(--primary-orange)', fontWeight: '700' }}>₹{parseFloat(item.sizes && item.sizes.length > 0 ? item.sizes[0].price : item.price || 0).toFixed(2)}</span>
                                                </div>
                                            </div>
                                            <button 
                                                onClick={() => onAddToCart && onAddToCart(item)}
                                                style={{ padding: '6px 12px', background: 'var(--primary-orange)', color: '#ffffff', border: 'none', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(234, 88, 12, 0.1)' }}
                                                onMouseEnter={(e) => e.currentTarget.style.filter = 'brightness(0.95)'}
                                                onMouseLeave={(e) => e.currentTarget.style.filter = 'none'}
                                            >
                                                + Add
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
    
                        <div className="cart-form">
                            {(dineInEnabled || takeawayEnabled || homeDeliveryEnabled) && !isWaiterMode && (
                                <div className="form-group">
                                    <label>Order Type:</label>
                                    <div className="order-type-selector">
                                        {dineInEnabled && (
                                            <>
                                                <input type="radio" id="dine-in" name="orderType" value="dine-in" checked={orderType === 'dine-in'} onChange={(e) => setOrderType(e.target.value)} />
                                                <label htmlFor="dine-in">Dine-in</label>
                                            </>
                                        )}
                                        {takeawayEnabled && (
                                            <>
                                                <input type="radio" id="takeaway" name="orderType" value="takeaway" checked={orderType === 'takeaway'} onChange={(e) => setOrderType(e.target.value)} />
                                                <label htmlFor="takeaway">Takeaway</label>
                                            </>
                                        )}
                                        {homeDeliveryEnabled && (
                                            <>
                                                <input type="radio" id="home-delivery" name="orderType" value="home-delivery" checked={orderType === 'home-delivery'} onChange={(e) => setOrderType(e.target.value)} />
                                                <label htmlFor="home-delivery">Home Delivery</label>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                            {isWaiterMode && (
                                <div className="form-group">
                                    <label>Order Type:</label>
                                    <div className="order-type-selector">
                                        <input type="radio" id="table-order" name="orderType" value="dine-in" checked={true} readOnly />
                                        <label htmlFor="table-order" style={{backgroundColor: 'var(--primary-orange)', color: '#fff'}}>Table Order (Captain)</label>
                                    </div>
                                </div>
                            )}
                            <div className="form-group">
                                <label htmlFor="customer-name">Name: <span style={{color: 'red'}}>*</span></label>
                                <input type="text" id="customer-name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Your Name" />
                            </div>
                            <div className="form-group">
                                <label htmlFor="customer-phone">Phone Number: <span style={{color: 'red'}}>*</span></label>
                                <input type="tel" id="customer-phone" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="Your Phone Number" />
                            </div>
                            {orderType === 'dine-in' ? (
                                <div className="form-group" id="table-number-group">
                                    <label htmlFor="table-number">Table Number: <span style={{color: 'red'}}>*</span></label>
                                    {isWaiterMode ? (
                                        <input type="text" id="table-number" value={tableNumber} onChange={(e) => setTableNumber(e.target.value)} placeholder="e.g. 5" />
                                    ) : (
                                        <select id="table-number" value={tableNumber} onChange={(e) => setTableNumber(e.target.value)}>
                                            {tableOptions}
                                        </select>
                                    )}
                                </div>
                            ) : null}
                            
                            {orderType === 'home-delivery' && homeDeliveryEnabled && (
                                <div className="form-group" id="delivery-address-group">
                                    <label htmlFor="delivery-address">Delivery Address: <span style={{color: 'red'}}>*</span></label>
                                    <textarea id="delivery-address" value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} placeholder="House/Flat No. & Building Name&#10;Street Name or Area&#10;Nearby Landmark&#10;City and Pincode"></textarea>
                                </div>
                            )}
                            <div className="form-group">
                                <label htmlFor="customer-message">Message (Optional):</label>
                                <textarea id="customer-message" value={customerMessage} onChange={(e) => setCustomerMessage(e.target.value)} placeholder="Any special instructions..."></textarea>
                            </div>
                        </div>

                        {couponsEnabled && (
                            <div className="cart-form coupon-section">
                                <div className="form-group">
                                    <label htmlFor="coupon-code">Coupon Code:</label>
                                    <div className="coupon-input-group">
                                        <input type="text" id="coupon-code" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} placeholder="Enter coupon"/>
                                        <button onClick={handleApplyCoupon}>Apply</button>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {loyaltyData && (
                            <div className="cart-form loyalty-section" style={{ backgroundColor: '#fff8f1', padding: '12px 15px', borderRadius: '8px', border: '1px solid #ea580c', margin: '0 15px 15px 15px' }}>
                                <h4 style={{ margin: '0 0 6px 0', color: '#ea580c', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem' }}>
                                    <i className="fas fa-gift"></i> Loyalty Program
                                </h4>
                                <p style={{ fontSize: '0.85rem', margin: '0 0 8px 0', color: 'var(--text-color)' }}>
                                    Hi <strong>{loyaltyData.name || 'there'}</strong>, you have <strong>{loyaltyData.points} points</strong> (Worth ₹{(loyaltyData.points * loyaltyData.rupees_per_point).toFixed(2)}).
                                </p>
                                
                                {loyaltyData.points >= loyaltyData.min_redemption_points ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.95rem' }}>
                                            <input 
                                                type="checkbox" 
                                                checked={redeemLoyalty} 
                                                onChange={(e) => {
                                                    setRedeemLoyalty(e.target.checked);
                                                    if (!e.target.checked) setIsPinVerified(false);
                                                }}
                                                style={{ width: '18px', height: '18px', accentColor: '#ea580c' }}
                                            />
                                            Redeem Points on this order
                                        </label>
                                        
                                        {redeemLoyalty && (
                                            <div style={{ marginTop: '5px', display: 'flex', gap: '10px' }}>
                                                <input 
                                                    type="password" 
                                                    placeholder="Enter 4-digit PIN" 
                                                    value={loyaltyPin}
                                                    onChange={(e) => {
                                                        setLoyaltyPin(e.target.value);
                                                        setIsPinVerified(false);
                                                    }}
                                                    disabled={isPinVerified}
                                                    style={{ flex: 1, padding: '12px', borderRadius: '5px', border: isPinVerified ? '1px solid var(--green-accent)' : '1px solid #cbd5e1', fontSize: '1rem', letterSpacing: '4px' }}
                                                    maxLength="4"
                                                />
                                                <button
                                                    onClick={handleVerifyPin}
                                                    disabled={isPinVerified || verifyingPin || loyaltyPin.length < 4}
                                                    style={{
                                                        padding: '0 20px',
                                                        backgroundColor: isPinVerified ? 'var(--green-accent)' : 'var(--primary-orange)',
                                                        color: '#fff',
                                                        border: 'none',
                                                        borderRadius: '5px',
                                                        cursor: isPinVerified ? 'default' : 'pointer',
                                                        fontWeight: 'bold',
                                                        opacity: (verifyingPin || (!isPinVerified && loyaltyPin.length < 4)) ? 0.7 : 1
                                                    }}
                                                >
                                                    {verifyingPin ? '...' : (isPinVerified ? 'Verified' : 'Verify')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p style={{ fontSize: '0.85rem', color: '#64748b', margin: 0 }}>
                                        You need {loyaltyData.min_redemption_points} points to redeem. Earn more on this order!
                                    </p>
                                )}
                            </div>
                        )}
                        
                        {homeDeliveryEnabled && orderType === 'home-delivery' && freeDeliveryThreshold > 0 && (
                            <div style={{ margin: '0 15px 15px 15px', backgroundColor: '#f8fafc', padding: '12px 15px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 'bold' }}>
                                    {subtotal >= freeDeliveryThreshold ? (
                                        <span style={{ color: '#16a34a' }}><i className="fas fa-gift" style={{ marginRight: '5px' }}></i> You've unlocked FREE Delivery!</span>
                                    ) : (
                                        <span style={{ color: '#334155' }}>Add ₹{(freeDeliveryThreshold - subtotal).toFixed(2)} more for FREE Delivery!</span>
                                    )}
                                </div>
                                <div style={{ width: '100%', height: '8px', backgroundColor: '#e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
                                    <div style={{ 
                                        height: '100%', 
                                        width: `${Math.min(100, (subtotal / freeDeliveryThreshold) * 100)}%`, 
                                        background: `linear-gradient(90deg, ${restaurantInfo?.theme_color || 'var(--primary-orange)'}88, ${restaurantInfo?.theme_color || 'var(--primary-orange)'})`,
                                        borderRadius: '10px',
                                        transition: 'width 0.3s ease-in-out'
                                    }}></div>
                                </div>
                            </div>
                        )}
    
                        <div className="cart-summary" id="cart-summary-container">
                            <div className="summary-row"><span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>
                            <div className="summary-row"><span>GST ({gstPercent}%)</span><span>₹{gst.toFixed(2)}</span></div>
                            {svcPercent > 0 && (
                                <div className="summary-row"><span>Service Charge ({svcPercent}%)</span><span>₹{serviceCharge.toFixed(2)}</span></div>
                            )}
                            {homeDeliveryEnabled && orderType === 'home-delivery' && (<div className="summary-row" id="delivery-fee-row"><span>Delivery Fee</span><span>{deliveryFee > 0 ? `₹${deliveryFee.toFixed(2)}` : 'Free'}</span></div>)}
                            {discount.type !== 'none' && (<div className="summary-row discount"><span>Discount ({discount.code})</span><span>-₹{discountAmount.toFixed(2)}</span></div>)}
                            {loyaltyDiscount > 0 && (<div className="summary-row discount" style={{color: '#ea580c'}}><span>Loyalty Redeemed</span><span>-₹{loyaltyDiscount.toFixed(2)}</span></div>)}
                            <div className="summary-row total"><span>Total</span><span>₹{total.toFixed(2)}</span></div>
                        </div>
    
                        <div className="cart-actions">
                            {!isRestaurantOpen && (
                                <div style={{ padding: '15px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: 'var(--border-radius-sm)', textAlign: 'center', fontWeight: '500', marginBottom: '10px' }}>
                                    This restaurant is currently not accepting orders.
                                </div>
                            )}
                            <div className="cart-checkout-actions" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                                <button 
                                    className="btn checkout-btn" 
                                    onClick={handleCheckoutClick} 
                                    disabled={!isRestaurantOpen || cart.length === 0}
                                    style={{ backgroundColor: 'var(--primary-orange)', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', borderRadius: '50px' }}
                                >
                                    {isRestaurantOpen ? <><i className={isWaiterMode ? "fas fa-paper-plane" : "fas fa-check-circle"}></i> {isWaiterMode ? 'Send Waiter Order to Kitchen' : 'Proceed to Checkout'}</> : 'Restaurant Closed'}
                                </button>
                                {isWaiterMode && (
                                    <button className="btn close-btn" onClick={onClose} style={{ borderRadius: '50px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#475569', padding: '12px', fontWeight: 'bold', cursor: 'pointer' }}>
                                        <i className="fas fa-times"></i> Cancel Order
                                    </button>
                                )}
                                <button className="btn clear-cart-btn" onClick={handleClearCart} style={{ borderRadius: '50px' }}>Clear Cart</button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Payment Mode Selection Modal */}
            {showPaymentModeModal && (
                <div className="cart-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000 }}>
                    <div className="cart-modal-content" style={{ background: '#fff', padding: '25px', borderRadius: '15px', width: '90%', maxWidth: '350px', textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                        <h3 style={{ marginTop: 0, color: 'var(--text-color-headings)', fontSize: '1.3rem', marginBottom: '20px' }}>Select Payment Mode</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <button onClick={() => confirmPaymentMode('online')} style={{ background: 'var(--primary-orange)', color: '#fff', border: 'none', padding: '15px', borderRadius: '8px', fontSize: '1.1rem', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                <i className="fas fa-qrcode"></i> Pay Now (UPI / Card)
                            </button>
                            <button onClick={() => confirmPaymentMode('cash')} style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', padding: '15px', borderRadius: '8px', fontSize: '1.1rem', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                <i className="fas fa-money-bill-wave"></i> {orderType === 'home-delivery' ? 'Cash on Delivery' : 'Pay at Counter'}
                            </button>
                            {whatsappEnabled && (
                                <button onClick={() => confirmPaymentMode('whatsapp')} style={{ background: '#25D366', color: '#fff', border: 'none', padding: '15px', borderRadius: '8px', fontSize: '1.1rem', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                    <i className="fab fa-whatsapp"></i> Order via WhatsApp
                                </button>
                            )}
                        </div>
                        <button onClick={() => setShowPaymentModeModal(false)} style={{ marginTop: '20px', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 'bold' }}>Cancel</button>
                    </div>
                </div>
            )}


        </aside>
    );
};

export default Cart;
